import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, ShieldAlert } from 'lucide-react';
import { AddButton, Drawer, Field, LiveScreen, QueryState, StatusPill, formatDate, type Column } from '@/components/live';
import { DataGrid } from '@/components/DataGrid';
import { StatCards, applyStat } from '@/components/StatCards';
import { callRpc } from '@/api/resources';
import { useAuth } from '@/auth/AuthProvider';
import { isSupabaseConfigured } from '@/lib/supabase';
import { toast } from '@/lib/toast';

interface OrgRow {
  organization_id: string;
  code: string;
  name: string;
  is_home: boolean;
  is_current: boolean;
  expires_at: string | null;
  period_from: string;
  period_to: string;
  plot_equivalent_ha: number;
  // The land meter: max(area, billable plots x k). Drafts are in neither side of it.
  billable_units: number;
  billable_plots: number;
  area_billable_ha: number;
  area_mapped_ha: number;
  area_declared_ha: number;
  // The shape behind the meter - why one number could never do.
  plots: number;
  plots_mapped: number;
  plots_unmapped: number;
  plots_4ha_plus: number;
  plots_under_4ha: number;
  smallest_plot_ha: number | null;
  largest_plot_ha: number | null;
  draft_plots: number;
  draft_area_ha: number;
  // The people meter.
  active_users: number;
  admins: number;
  farmers: number;
  farmers_with_plots: number;
  farmers_no_plots: number;
  parties: number;
  // The rest.
  open_approvals: number;
  storage_mb: number;
  farmers_added: number;
  plots_added: number;
  harvest_entries: number;
  messages_sent: number;
  sat_jobs_completed: number;
  sat_baselines: number;
  sat_rechecks: number;
  sat_snapshots: number;
  sat_harvest_checks: number;
  sat_jobs_failed: number;
  documents_added: number;
}

/** First of the current month, in the local calendar rather than UTC. */
function monthStart(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
}
function today(): string {
  return new Date().toLocaleDateString('en-CA');
}

/**
 * The organisations this account may work in.
 *
 * For everyone except a platform operator that is exactly one - their own - so the screen says
 * so plainly rather than showing a one-row table that implies there could be others.
 *
 * A platform operator adds an organisation here (owner, 2026-09-27: "take the UI route to add
 * ISRI"). `api.fn_create_organization` does the whole setup in one transaction - tenant, roles
 * and their grants, every reference set, geography, satellite and messaging providers (all off) -
 * so a failure leaves nothing half-made. It also gives the creator an operator grant, so the new
 * organisation appears in the switcher at once; its first administrator is invited from inside it,
 * on Access & scope, like any other user.
 */
export default function OrganisationsScreen() {
  const { profile } = useAuth();
  const [stat, setStat] = useState<string | null>(null);
  // Billing runs by period, so the period is a control rather than a constant. It defaults to the
  // month in progress, which is the question asked most often.
  const [from, setFrom] = useState(monthStart);
  const [to, setTo] = useState(today);
  // k: the hectares whose work costs about what handling one plot costs. It is the only number
  // that changes the meter's shape, so it is a control rather than a constant.
  const [k, setK] = useState('1');

  // fn_organization_overview is fn_my_organizations plus a count of what is inside each one. Its
  // visibility is the same to the letter - your own organisation, plus each LIVE operator grant -
  // so revoking a grant takes the numbers away at the next request, not just the ability to enter.
  const orgs = useQuery({
    queryKey: ['organization-overview', from, to, k],
    queryFn: () => callRpc<OrgRow[]>('fn_organization_overview', {
      p_from: from, p_to: to, p_plot_equivalent_ha: Number(k) || 0,
    }),
    enabled: isSupabaseConfigured && Boolean(profile),
  });

  const rows = orgs.data ?? [];
  const operator = rows.length > 1;

  const client = useQueryClient();
  const blank = { name: '', code: '', website: '' };
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState(blank);
  const [error, setError] = useState<string>();
  const create = useMutation({
    mutationFn: () => callRpc<string>('fn_create_organization', {
      p_name: draft.name, p_code: draft.code, p_website: draft.website || null,
    }),
    onSuccess: () => {
      toast.success(`${draft.name.trim()} is set up. Enter it from the switcher to invite its first administrator.`);
      client.invalidateQueries({ queryKey: ['organization-overview'] });
      client.invalidateQueries({ queryKey: ['my-organizations'] });
      setAdding(false);
      setDraft(blank);
    },
    onError: (caught: Error) => setError(caught.message),
  });

  // Two kinds of card. The relationship ones say how this account reaches each organisation; the
  // rest are the reasons an operator would go and look. "No administrator" is the sharpest of
  // them: since migration 0130 only the app owner can create one, so a customer sitting at zero
  // is waiting on the person reading this screen and on nobody else.
  const STATS = useMemo(() => [
    { key: 'all', label: 'Organisations', match: () => true, tone: 'neutral' as const },
    { key: 'home', label: 'Home', tone: 'ok' as const, match: (row: OrgRow) => row.is_home },
    { key: 'support', label: 'Support access', tone: 'info' as const, match: (row: OrgRow) => !row.is_home },
    { key: 'current', label: 'Working here', tone: 'info' as const, match: (row: OrgRow) => row.is_current },
    { key: 'no_admin', label: 'No administrator', tone: 'error' as const,
      match: (row: OrgRow) => row.admins === 0 },
    { key: 'unmapped', label: 'Area part declared', tone: 'warn' as const,
      match: (row: OrgRow) => row.area_declared_ha > row.area_mapped_ha },
    { key: 'polygon_bound', label: 'Polygons outweigh area', tone: 'info' as const,
      match: (row: OrgRow) => row.billable_plots * row.plot_equivalent_ha > row.area_billable_ha },
    { key: 'has_drafts', label: 'Unfinished plots', tone: 'warn' as const,
      match: (row: OrgRow) => row.draft_plots > 0 },
    { key: 'one_admin', label: 'One administrator', tone: 'warn' as const,
      match: (row: OrgRow) => row.admins === 1 },
    { key: 'empty', label: 'Nothing recorded yet', tone: 'warn' as const,
      match: (row: OrgRow) => row.farmers === 0 && row.plots === 0 },
    { key: 'waiting', label: 'Awaiting approval', tone: 'warn' as const,
      match: (row: OrgRow) => row.open_approvals > 0 },
    { key: 'no_expiry', label: 'Support with no end date', tone: 'warn' as const,
      match: (row: OrgRow) => !row.is_home && !row.expires_at },
  ], []);

  const shown = applyStat(rows, STATS, stat);
  const total = (key: keyof OrgRow) =>
    shown.reduce((sum, row) => sum + Number(row[key] ?? 0), 0);

  const columns: Array<Column<OrgRow & { id?: string }>> = [
    { key: 'name', label: 'Organisation', render: (row) => (
      <div>
        <strong>{row.name}</strong>
        <div className="muted">{row.code}</div>
      </div>
    ) },
    {
      key: 'is_home', label: 'Relationship',
      render: (row) => (row.is_home
        ? <StatusPill value="active" />
        : <span className="muted"><ShieldAlert size={13} /> Support access</span>),
    },
    {
      key: 'is_current', label: 'Now',
      render: (row) => (row.is_current ? <strong>Working here</strong> : <span className="muted">--</span>),
    },
    {
      // The meter, with both candidates underneath: whichever is larger is what was charged, and
      // the reader can see which one bound without doing the arithmetic.
      key: 'billable_units', label: 'Billable units',
      render: (row) => {
        const polygonSide = row.billable_plots * Number(row.plot_equivalent_ha);
        const bindsOnPolygons = polygonSide > Number(row.area_billable_ha);
        return Number(row.billable_units) === 0 ? <span className="muted">--</span> : (
          <>
            <strong>{Number(row.billable_units).toFixed(2)}</strong>
            <div className="muted">
              {bindsOnPolygons
                ? `${row.billable_plots} plots x ${row.plot_equivalent_ha}`
                : `${Number(row.area_billable_ha).toFixed(2)} ha`} binds
            </div>
          </>
        );
      },
    },
    {
      key: 'area_billable_ha', label: 'Area (ha)',
      render: (row) => (Number(row.area_billable_ha) === 0 ? <span className="muted">--</span>
        : <>
            {Number(row.area_billable_ha).toFixed(2)}
            <div className="muted">
              {Number(row.area_mapped_ha).toFixed(2)} mapped
              {Number(row.area_billable_ha) > Number(row.area_mapped_ha)
                ? `, ${(Number(row.area_billable_ha) - Number(row.area_mapped_ha)).toFixed(2)} declared only`
                : ''}
            </div>
          </>),
    },
    {
      key: 'billable_plots', label: 'Plots',
      render: (row) => (row.billable_plots === 0 && row.draft_plots === 0 ? <span className="muted">--</span>
        : <>{row.billable_plots}
            <div className="muted">
              {row.plots_mapped} mapped
              {row.plots_unmapped > 0 ? `, ${row.plots_unmapped} not` : ''}
              {row.draft_plots > 0 ? `, ${row.draft_plots} draft` : ''}
            </div>
          </>),
    },
    {
      // The spread is the argument for the meter: one farmer here holds 0.2 and 50 ha.
      key: 'smallest_plot_ha', label: 'Plot size range',
      render: (row) => (row.smallest_plot_ha === null ? <span className="muted">--</span>
        : <>{Number(row.smallest_plot_ha).toFixed(2)} to {Number(row.largest_plot_ha).toFixed(2)} ha
            <div className="muted">{row.plots_4ha_plus} at 4 ha or more, {row.plots_under_4ha} under</div>
          </>),
    },
    {
      key: 'active_users', label: 'Active users',
      render: (row) => (row.active_users === 0
        ? <span className="state-note is-warn">Nobody yet</span>
        : <>{row.active_users}<div className="muted">{row.admins === 0
            ? 'no administrator'
            : `${row.admins} admin${row.admins === 1 ? '' : 's'}`}</div></>),
    },
    {
      key: 'farmers', label: 'Farmers',
      render: (row) => (row.farmers === 0 ? <span className="muted">--</span>
        : <>{row.farmers}{row.farmers_no_plots > 0 && <div className="muted">{row.farmers_no_plots} with no land</div>}</>),
    },
    { key: 'parties', label: 'Parties', render: (row) => row.parties || <span className="muted">--</span> },
    {
      key: 'storage_mb', label: 'Storage (MB)',
      render: (row) => (Number(row.storage_mb) === 0 ? <span className="muted">--</span>
        : Number(row.storage_mb).toFixed(1)),
    },
    {
      key: 'farmers_added', label: 'Added in period',
      render: (row) => ((row.farmers_added + row.plots_added) === 0 ? <span className="muted">--</span>
        : <>{row.farmers_added} farmers<div className="muted">{row.plots_added} plots</div></>),
    },
    { key: 'messages_sent', label: 'Messages sent', render: (row) => (row.messages_sent || <span className="muted">--</span>) },
    {
      // Every kind, because a baseline, a monthly snapshot and a harvest assessment plausibly do
      // not cost the same. Failures are shown and not billed.
      key: 'sat_jobs_completed', label: 'Satellite jobs',
      render: (row) => (row.sat_jobs_completed === 0 && row.sat_jobs_failed === 0
        ? <span className="muted">--</span>
        : <>{row.sat_jobs_completed}
            <div className="muted">
              {row.sat_baselines}b / {row.sat_rechecks}r / {row.sat_snapshots}s / {row.sat_harvest_checks}h
              {row.sat_jobs_failed > 0 ? `, ${row.sat_jobs_failed} failed` : ''}
            </div>
          </>),
    },
    { key: 'harvest_entries', label: 'Harvests', render: (row) => (row.harvest_entries || <span className="muted">--</span>) },
    {
      key: 'open_approvals', label: 'Awaiting approval',
      render: (row) => (row.open_approvals === 0 ? <span className="muted">--</span> : <strong>{row.open_approvals}</strong>),
    },
    {
      key: 'expires_at', label: 'Access ends',
      render: (row) => (row.is_home ? <span className="muted">--</span>
                                    : row.expires_at ? formatDate(row.expires_at) : 'No expiry set'),
    },
  ];

  return (
    <LiveScreen
      actions={operator
        ? <AddButton label="Add organisation" onClick={() => { setError(undefined); setAdding(true); }} />
        : undefined}
      subtitle={operator
        ? 'Your own organisation, and the customers you hold support access to.'
        : 'The organisation this account belongs to.'}
      title="Organisations"
    >
      <QueryState empty="No organisation is linked to this account." query={orgs}>
        {operator ? (
          <>
            <p className="offline-banner">
              <Building2 size={13} /> Entering a customer is recorded in <em>their</em> audit trail
              with your name and your reason, and it ends on its own. Switch from the picker in the
              header. These are counts only - who the farmers are means entering the organisation.
            </p>
            <div className="billing-period">
              <Field label="Period from">
                <input max={to} onChange={(event) => setFrom(event.target.value)} type="date" value={from} />
              </Field>
              <Field label="to">
                <input min={from} onChange={(event) => setTo(event.target.value)} type="date" value={to} />
              </Field>
              <Field hint="Hectares one plot is worth" label="k">
                <input inputMode="decimal" min="0" onChange={(event) => setK(event.target.value)}
                       step="0.25" type="number" value={k} />
              </Field>
              <p className="muted">
                Seats, farmers, plots and area are read as they stand now. Added, messages,
                satellite checks and harvests are counted inside the period. k is the hectares whose
                work costs about what handling one plot costs: at 0 the meter is pure area, high
                enough and it is pure polygon count.
              </p>
            </div>
            <StatCards active={stat} onSelect={setStat} rows={rows} stats={STATS} />
            <DataGrid
              exportName={`organisations-${from}-to-${to}`} recordNoun={'organisation'}
              searchPlaceholder={'Organisation name or code'}
              columns={columns}
              rows={applyStat(rows, STATS, stat).map((row) => ({ ...row, id: row.organization_id }))}
            />
            <div className="card billing-total">
              <h3>Across {shown.length} organisation{shown.length === 1 ? '' : 's'}</h3>
              <dl>
                <div><dt>Billable units</dt><dd>{total('billable_units').toFixed(2)}</dd></div>
                <div><dt>Active users</dt><dd>{total('active_users')}</dd></div>
                <div><dt>Farmers</dt><dd>{total('farmers')}</dd></div>
                <div><dt>Plots</dt><dd>{total('billable_plots')}</dd></div>
                <div><dt>Area (ha)</dt><dd>{total('area_billable_ha').toFixed(2)}</dd></div>
                <div><dt>Messages sent</dt><dd>{total('messages_sent')}</dd></div>
                <div><dt>Satellite jobs</dt><dd>{total('sat_jobs_completed')}</dd></div>
                <div><dt>Storage (MB)</dt><dd>{total('storage_mb').toFixed(1)}</dd></div>
              </dl>
              <p className="muted">
                Whatever the cards are filtering, these are the rows below. Billable units are
                max(area, plots &times; k) per organisation and cannot be summed from the totals
                above &mdash; each organisation's own binding side may differ. Drafts are in no
                billing figure. Export carries every column.
              </p>
            </div>
          </>
        ) : (
          <>
            <StatCards active={stat} onSelect={setStat} rows={rows} stats={STATS} />
            <div className="card">
              <h2>{rows[0]?.name ?? profile?.organization?.name ?? 'Your organisation'}</h2>
              <p className="muted">
                {rows[0]?.code ?? profile?.organization?.code} &middot; this account works here and
                nowhere else. Organisations are separated at the database, not in the interface:
                there is no setting that would let this account read another one&rsquo;s records.
              </p>
            </div>
          </>
        )}
      </QueryState>

      <Drawer
        footer={
          <>
            <button className="cancel" onClick={() => setAdding(false)} type="button">Cancel</button>
            <button className="primary" disabled={create.isPending || !draft.name.trim() || !draft.code.trim()}
                    onClick={() => { setError(undefined); create.mutate(); }} type="button">
              {create.isPending ? 'Setting up...' : 'Add organisation'}
            </button>
          </>
        }
        onClose={() => setAdding(false)}
        open={adding}
        subtitle="Sets up roles, reference lists, geography, and satellite and messaging providers (all off). Takes about half a minute."
        title="Add an organisation"
      >
        <Field hint="As registered, e.g. with Pvt Ltd. It heads the letterhead on every document." label="Full name" required>
          <input onChange={(event) => setDraft({ ...draft, name: event.target.value })} value={draft.name} />
        </Field>
        <Field hint="2 to 20 capital letters, digits, hyphens or underscores. Cannot be changed later." label="Code" required>
          <input maxLength={20} onChange={(event) => setDraft({ ...draft, code: event.target.value.toUpperCase() })}
                 value={draft.code} />
        </Field>
        <Field label="Website">
          <input inputMode="url" onChange={(event) => setDraft({ ...draft, website: event.target.value })}
                 placeholder="www.example.org" value={draft.website} />
        </Field>
        <p className="muted">
          You get support access to it straight away. Its first administrator is invited from inside
          it, on Access &amp; scope, and satellite monitoring and messaging are switched on there too.
        </p>
        {error && <p className="login-error" role="alert">{error}</p>}
      </Drawer>
    </LiveScreen>
  );
}
